/**
 * These are the individual rows in a filter panel.
 */
import { CloseOutlined, StopOutlined } from '@ant-design/icons'
import { Button, DatePicker, Input, InputNumber, Select, Tooltip } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useDrilldownSchema } from 'features/drilldown'
import { DrilldownTypeAndFieldSelect } from 'features/drilldown/components/fieldSelect/TypeAndFieldSelect'
import { usePreexistingEnumSelectOptions } from 'features/drilldown/components/ontology/utils'
import { FieldDef } from 'features/drilldown/rolodex/DrilldownDataSchema'
import {
  DrilldownField,
  DrilldownFieldType,
} from 'features/drilldown/rolodex/ontology/ontologyTypes'
import React, {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { RolodexHistogramFilter, RolodexHistogramFilterOp } from 'types'

import { Filter } from './DrilldownFilterView'

export type DrilldownFilterRowProps = {
  filter: Filter
  onRemoveFilter: () => void
  setFilterState: Dispatch<SetStateAction<Filter['state']>>
  isActive: boolean
  allowFilterOps: ReadonlyArray<RolodexHistogramFilterOp> | undefined
}

const FilterRowContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`

const FilterWarningIcon = styled(StopOutlined)`
  font-size: 16px;
  color: ${CantileverTheme.colors.DeepRed};
  margin-left: 8px;
`

const FilterFieldSelect = styled(DrilldownTypeAndFieldSelect)`
  min-width: 200px;
`

const FilterOperatorSelect = styled(Select)`
  min-width: 130px;
`

const FilterValueContainer = styled.div<{ $isValueEditable: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: row;
  min-width: ${({ $isValueEditable }) => ($isValueEditable ? '400px' : '50px')};
  max-width: 700px;
  gap: 0.5rem;
`

const RemoveFilterButton = styled(Button)`
  color: ${CantileverTheme.colors.gray['600']};
  transition: all 0.2s ease;

  &:hover {
    color: ${CantileverTheme.colors.DeepRed};
  }
`

/**
 * Display names to show in the 'operator' select
 */
export const OPERATOR_NAMES: Record<RolodexHistogramFilterOp, string> = {
  '=': 'Equals',
  '!=': 'Not Equal to',
  '~': 'Contains',
  '>': 'Greater than',
  '<': 'Less than',
  '>=': 'Greater than or equal to',
  '<=': 'Less than or equal to',
  in: 'In',
  notin: 'Not in',
  null: 'Is Null',
  notnull: 'Is not Null',
  contains: 'Contains (Array)', // Postgres: @>
  truthy: 'True',
  falsy: 'False',
}

/**
 * Which operators to show for each field type
 */
export const FIELD_TYPE_OPERATORS: Record<
  DrilldownFieldType,
  readonly RolodexHistogramFilter['op'][]
> = {
  text: ['~', '=', '!='],
  number: ['=', '!=', '>', '<', '<=', '>='],
  enum: ['in', 'notin'],
  boolean: ['truthy', 'falsy'],
  date: ['=', '!=', '<', '>', '<=', '>='],
  // TODO: Add support for object and array filters in the future
  object: [],
  array: [],
}

/**
 * Operators to show for 'unary' fields, which don't require a value input.
 */
export const UNARY_OPERATORS: readonly RolodexHistogramFilterOp[] = [
  'null',
  'notnull',
  'truthy',
  'falsy',
]

/**
 * Operators that are allowed for all property types.
 */
export const GLOBAL_OPERATORS: readonly RolodexHistogramFilterOp[] = [
  'null',
  'notnull',
]

type FilterValue = (string | number) | (string | number)[] | boolean | null

export interface FilterState {
  fieldDef: FieldDef
  field: DrilldownField
  operator: RolodexHistogramFilterOp
  value?: FilterValue
}

/**
 * This component handles the selection for a field and an operator,
 * and then renders the appropriate value-entry component based on the
 * type of the field.
 * @returns the filter row
 */
const RawDrilldownFilterRow: FC<DrilldownFilterRowProps> = ({
  filter,
  onRemoveFilter,
  setFilterState,
  isActive,
  allowFilterOps,
}) => {
  const { state: filterState } = filter
  const { operator, field, fieldDef, value } = filterState

  const { fields } = useDrilldownSchema()

  const { fieldType } = fieldDef

  const possibleOperators = useMemo(
    () => [...(FIELD_TYPE_OPERATORS[fieldType] ?? []), ...GLOBAL_OPERATORS],
    [fieldType]
  )
  // If operator is invalid, reset to the first valid operator for the field
  useEffect(() => {
    if (!possibleOperators.includes(operator)) {
      setFilterState((prev) => ({ ...prev, operator: possibleOperators[0] }))
    }
  }, [fieldType, fields, operator, possibleOperators, setFilterState])

  const operatorOptions = useMemo(
    () =>
      possibleOperators
        .filter((op) => !allowFilterOps || allowFilterOps.includes(op))
        .map((operator) => ({
          value: operator,
          label: OPERATOR_NAMES[operator],
        })),
    [allowFilterOps, possibleOperators]
  )

  const FilterComponent: FC<FilterInputProps> = UNARY_OPERATORS.includes(
    operator
  )
    ? UnaryFilterComponent
    : FILTER_RENDERS[fieldType]

  const isValueEditable = useMemo(() => {
    return !UNARY_OPERATORS.includes(operator)
  }, [operator])

  const setSelectedField = useCallback(
    (val: SetStateAction<DrilldownField>) => {
      setFilterState((prev) => {
        const oldVal = prev.field
        const selectedField = typeof val === 'function' ? val(oldVal) : val
        const fieldType = fields[selectedField]?.fieldType ?? 'text'
        const operators = FIELD_TYPE_OPERATORS[fieldType]
        const shouldKeepOperator = operators?.includes(operator)
        return {
          field: selectedField,
          operator: shouldKeepOperator ? operator : operators[0],
          fieldDef: fields[selectedField],
          value: shouldKeepOperator ? value : undefined,
        }
      })
    },
    [fields, operator, setFilterState, value]
  )

  return (
    <FilterRowContainer>
      <FilterFieldSelect
        selectedField={field}
        setSelectedField={setSelectedField}
      />
      <FilterOperatorSelect
        value={operator}
        options={operatorOptions}
        onChange={(value) => {
          setFilterState((prev) => ({
            ...prev,
            operator: value as RolodexHistogramFilterOp,
            value: undefined,
          }))
        }}
        popupMatchSelectWidth={false}
      />
      <FilterValueContainer $isValueEditable={isValueEditable}>
        <FilterComponent filter={filter} setFilterState={setFilterState} />
        {!isActive && (
          <Tooltip title='Filter not applied - please supply a value'>
            <FilterWarningIcon />
          </Tooltip>
        )}
        <RemoveFilterButton type='text' onClick={onRemoveFilter}>
          <CloseOutlined />
        </RemoveFilterButton>
      </FilterValueContainer>
    </FilterRowContainer>
  )
}

/**
 * Memo'd filter row (keeps things feeling snappy when you have a lot of them).
 */
export const DrilldownFilterRow = React.memo(RawDrilldownFilterRow)

function useSetFilterValue(
  setFilterState: Dispatch<SetStateAction<FilterState>>
): Dispatch<SetStateAction<Filter['state']['value']>> {
  return useCallback(
    (val: SetStateAction<Filter['state']['value']>) => {
      setFilterState((prevFilter) => ({
        ...prevFilter,
        value: typeof val === 'function' ? val(prevFilter.value) : val,
      }))
    },
    [setFilterState]
  )
}

interface FilterInputProps {
  filter: Filter
  setFilterState: Dispatch<SetStateAction<FilterState>>
}

const DrilldownNumberInput: FC<FilterInputProps> = ({
  filter,
  setFilterState,
}) => {
  const onChange = useSetFilterValue(setFilterState)
  return (
    <InputNumber value={filter.state.value as number} onChange={onChange} />
  )
}

const DrilldownTextInput: FC<FilterInputProps> = ({
  filter,
  setFilterState,
}) => {
  const updateFilterValue = useSetFilterValue(setFilterState)
  return (
    <Input
      value={filter.state.value as string}
      onChange={(e) => updateFilterValue(e.target.value as string)}
    />
  )
}

const UnaryFilterComponent: FC<FilterInputProps> = () => {
  return <></>
}

/**
 * A filter row for the entry of an 'enum' value - it runs a Rolodex query
 * to determine valid values, and renders a multi-select for a user to toggle
 * them.
 * @param props component props
 * @returns the component
 */
const DrilldownEnumInput: FC<FilterInputProps> = ({
  filter,
  setFilterState,
}) => {
  const { field } = filter.state
  const { fieldType } = filter.state.fieldDef

  // Once the ontology is richer for each impl, we should switch this to call
  // `useOntologyEnumSelectOptions` for the ontology enum values rather than the preexisting ones.
  const { enumOptions: selectOptions, isLoadingEnumValues } =
    usePreexistingEnumSelectOptions(fieldType, field)

  const value = filter.state.value
  const setValue = useSetFilterValue(setFilterState)
  const selectedValues = useMemo(
    () => (Array.isArray(value) && value ? value : []) as string[],
    [value]
  )

  // Reset selections when options change (if selections are now invalid)
  useEffect(() => {
    const areAllValuesValid = selectedValues.every((v) =>
      selectOptions?.map((option) => option.value).includes(v)
    )
    const areOptionsPresent = selectOptions?.length // Don't reset filters when loading just because options aren't present yet
    if (!areAllValuesValid && areOptionsPresent) {
      setValue([])
    }
  }, [selectOptions, selectedValues, setValue])

  return (
    <Select
      loading={isLoadingEnumValues}
      disabled={!selectOptions?.length}
      defaultValue={selectedValues}
      style={{ width: '100% ' }}
      mode='multiple'
      allowClear
      onChange={(newSelected) => {
        setValue([...newSelected])
      }}
      options={selectOptions}
    />
  )
}

const DrilldownDateInput: FC<FilterInputProps> = ({
  filter: {
    state: { value },
  },
  setFilterState,
}) => {
  const setValue = useSetFilterValue(setFilterState)
  const dateValue = useMemo(
    () => (value ? dayjs(value.toString(), 'YYYY-MM-DD') : undefined),
    [value]
  )
  const onPickDate = useCallback(
    (date: Dayjs, dateStr: string | string[]) => {
      setValue(
        date ? (Array.isArray(dateStr) ? dateStr[0] : dateStr) : undefined
      )
    },
    [setValue]
  )
  return <DatePicker onChange={onPickDate} value={dateValue} />
}

/**
 * Mapping of FieldType to which value-entry component to render.
 */
const FILTER_RENDERS: Record<DrilldownFieldType, FC<FilterInputProps>> = {
  text: memo(DrilldownTextInput),
  number: memo(DrilldownNumberInput),
  enum: memo(DrilldownEnumInput),
  boolean: memo(UnaryFilterComponent),
  date: memo(DrilldownDateInput),
  // TODO: In the future - implement object and array components for filters
  object: () => <></>,
  array: () => <></>,
}
